import {
    COMMON_METABASE_CONFIG,
    DashboardEmbedType,
} from "@common-shared/config/metabase.config.ts"

import { GENERAL_CONFIG } from "../config/general.config"
import { INSIGHTS_CONFIG } from "../config/insights.config"
import { ROUTES_CONFIG } from "../config/routes.config"
import { UtilHelper } from "./util.helper"

export class InsightsHelper {
    /**
     * Resolve which Metabase embed to show from Insights page query params.
     * `dashboard` takes precedence over `question` when both are present.
     * Falls back to the default Insights dashboard when neither is valid.
     */
    static getEmbedFromSearchParams(searchParams: URLSearchParams): {
        embedType: DashboardEmbedType
        embedId: number
        isCustomEmbed: boolean
    } {
        const dashboardId = UtilHelper.parsePositiveInt(
            searchParams.get(GENERAL_CONFIG.urlSearchParamsKeys.dashboard),
        )

        if (dashboardId !== null) {
            return {
                embedType: DashboardEmbedType.DASHBOARD,
                embedId: dashboardId,
                isCustomEmbed: true,
            }
        }

        const questionId = UtilHelper.parsePositiveInt(
            searchParams.get(GENERAL_CONFIG.urlSearchParamsKeys.question),
        )

        if (questionId !== null) {
            return {
                embedType: DashboardEmbedType.QUESTION,
                embedId: questionId,
                isCustomEmbed: true,
            }
        }

        return {
            embedType: DashboardEmbedType.DASHBOARD,
            embedId: COMMON_METABASE_CONFIG.embeds.dashboards.oneVerifyMain,
            isCustomEmbed: false,
        }
    }

    /**
     * Safe in-app return path from the `from` query param, if present.
     */
    static getSafeFromPath(searchParams: URLSearchParams): string | null {
        const fromPath = searchParams.get(
            GENERAL_CONFIG.urlSearchParamsKeys.from,
        )

        if (
            !fromPath ||
            !fromPath.startsWith("/") ||
            fromPath.startsWith("//")
        ) {
            return null
        }

        return fromPath
    }

    /**
     * Back-button label for a custom Insights embed, based on the `from` path.
     */
    static getBackLabel(fromPath: string | null): string {
        const pathname = fromPath?.split("?")[0] ?? null

        const destinationLabel = pathname
            ? INSIGHTS_CONFIG.fromPathLabels[pathname]
            : undefined

        return destinationLabel ? `Back to ${destinationLabel}` : "Back"
    }

    /**
     * Build an Insights URL, optionally targeting a specific dashboard or question.
     * Prefer `dashboardId` when both are provided.
     */
    static buildInsightsUrl(options?: {
        dashboardId?: number
        questionId?: number
        fromPath?: string
    }): string {
        const searchParams = new URLSearchParams()

        if (options?.dashboardId) {
            searchParams.set(
                GENERAL_CONFIG.urlSearchParamsKeys.dashboard,
                String(options.dashboardId),
            )
        } else if (options?.questionId) {
            searchParams.set(
                GENERAL_CONFIG.urlSearchParamsKeys.question,
                String(options.questionId),
            )
        }

        if (
            options?.fromPath &&
            options.fromPath.startsWith("/") &&
            !options.fromPath.startsWith("//")
        ) {
            searchParams.set(
                GENERAL_CONFIG.urlSearchParamsKeys.from,
                options.fromPath,
            )
        }

        const query = searchParams.toString()

        return query
            ? `${ROUTES_CONFIG.insightsUrl}?${query}`
            : ROUTES_CONFIG.insightsUrl
    }

    /**
     * Insights URL for the ERA upload status Metabase dashboard.
     */
    static buildEraUploadStatusInsightsUrl(): string {
        return InsightsHelper.buildInsightsUrl({
            dashboardId:
                COMMON_METABASE_CONFIG.embeds.dashboards
                    .oneVerifyEraUploadStatus,
            fromPath: ROUTES_CONFIG.integrationsUrl,
        })
    }
}
